import { useEffect, useState } from "react";

import {
  DatePicker,
  TextArea,
  TextField,
  Select,
  Checkbox,
  DocumentList,
} from "@king-ict/eupisi-ui/components";
import { codeBookToSelectData, nameof } from "@king-ict/eupisi-ui/utils";
import { Row, Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";

import { CommissionSelection } from "./components";
import { DEFAULT_REQUEST } from "./constants";
import {
  getRequestDocument,
  getDecisionProposal,
  saveDecisionProposal,
  getForm7,
  getRequest,
  getCommitteesForDocument,
} from "../../api/Request/RequestApi";
import { useAppDispatch } from "../../app/store";
import {
  ContentArea,
  ToolbarTop,
  ToolbarEdit,
  FormCard,
  RequestHeader,
} from "../../components";
import { useAuth } from "../../hooks/auth/useAuth";
import { ValidationFailure, Role } from "../../models";
import {
  Request,
  RequestType,
  DocumentType,
  DecisionProposal,
  Form7,
  DocumentCommittee,
  RequestStatus,
} from "../../models/Request";
import { Attachment } from "../../models/Shared/Attachment";
import { useGetDifficultyTypeListQuery } from "../../queries/codeBookQuery";
import { requestAdminDetailsUrl } from "../../routes/routes";
import { resetIsEdited, setIsEdited } from "../../slices/applicationSlice";

export function DecisionProposalEdit() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const auth = useAuth();

  const createDefaultDecisionProposal = (): DecisionProposal => {
    return {
      id: 0,
      requestId: +id,
      determinationStartDate: new Date(),
      determinationEndDate: new Date(),
      sessionDate: new Date(),
      isEarlyEnrollmentApproved: false,
      isEarlyEnrollmentRejected: false,
      isEnrollmentPostponement: false,
      isAppropriateProgram: false,
      isPreparatoryCourse: false,
      request: DEFAULT_REQUEST,
      committeesForDocument: [],
    };
  };
  const [item, setItem] = useState<Request>(DEFAULT_REQUEST);
  const [decision, setDecision] = useState<DecisionProposal>(
    createDefaultDecisionProposal()
  );
  const [form7, setForm7] = useState<Form7>();

  const { data: difficultyTypes } = useGetDifficultyTypeListQuery();

  const [isEditing, setIsEditing] = useState(false);
  const [errors, setErrors] = useState<ValidationFailure[]>();
  const [initialDocumentCommittees, setInitialDocumentCommittees] =
    useState<DocumentCommittee[]>();

  useEffect(() => {
    (async () => {
      setIsEditing(true); // Assume we don't get one.
      if (id && id !== "0") {
        const result = await getDecisionProposal(+id);

        if (result) {
          setDecision(result);
          setIsEditing(false);
          setInitialDocumentCommittees(result.committeesForDocument);

          setItem(result.request);
        } else {
          const request = await getRequest(+id);

          if (request) {
            setItem(request);

            const committeesForDocument = await getCommitteesForDocument(
              +id,
              DocumentType.DecisionProposal
            );

            setInitialDocumentCommittees(committeesForDocument);
            setDecision({
              ...decision,
              request,
              committeesForDocument,
            });
          }
        }

        const form7Item = await getForm7(+id);
        if (form7Item) {
          setForm7(form7Item);
        }
      }
    })();
    dispatch(resetIsEdited());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const canEdit = () => {
    return (
      !isEditing &&
      !form7 &&
      item.statusId !== RequestStatus.DecisionSuspended &&
      (auth.currentRole?.role === Role.CAS ||
        auth.currentRole?.role === Role.Helpdesk ||
        auth.currentRole?.role === Role.InstitutionPrincipal ||
        auth.currentRole?.role === Role.InstitutionAdministrator ||
        (auth.currentRole?.role === Role.InstitutionCommittee &&
          (auth
            .getCommitteeMembershipByCommitteeId(
              item.institutionCommitteeId ?? 0
            )
            ?.some((v) => v.isPresident) ??
            false)))
    );
  };
  const handleEditingClick = () => {
    setIsEditing(true);
  };
  const handleDeterminationStartDateChanged = (val: Date) => {
    const d: DecisionProposal = {
      ...decision,
      determinationStartDate: val,
    };
    handleUpdate(d);
  };
  const handleDeterminationEndDateChanged = (val: Date) => {
    const d: DecisionProposal = {
      ...decision,
      determinationEndDate: val,
    };
    handleUpdate(d);
  };
  const handleSessionDateChanged = (val: Date) => {
    const d: DecisionProposal = {
      ...decision,
      sessionDate: val,
    };
    handleUpdate(d);
  };
  const handleIsEarlyEnrollmentApprovedChange = (val: boolean) => {
    const d: DecisionProposal = {
      ...decision,
      isEarlyEnrollmentApproved: val,
      isEarlyEnrollmentRejected: false,
    };
    handleUpdate(d);
  };
  const handleIsEarlyEnrollmentRejectedChange = (val: boolean) => {
    const d: DecisionProposal = {
      ...decision,
      isEarlyEnrollmentApproved: false,
      isEarlyEnrollmentRejected: val,
    };
    handleUpdate(d);
  };
  const handleIsEnrollmentPostponementChange = (val: boolean) => {
    const d: DecisionProposal = {
      ...decision,
      isEnrollmentPostponement: val,
    };
    handleUpdate(d);
  };
  const handleIsAppropriateProgramChange = (val: boolean) => {
    const d: DecisionProposal = {
      ...decision,
      isAppropriateProgram: val,
    };
    handleUpdate(d);
  };
  const handleIsPreparatoryCourseChange = (val: boolean) => {
    const d: DecisionProposal = {
      ...decision,
      isPreparatoryCourse: val,
    };
    handleUpdate(d);
  };
  const handlePracticalMethodCheckedChange = (val: boolean) => {
    const d: DecisionProposal = {
      ...decision,
      practicalMethod: val ? "" : undefined,
    };
    handleUpdate(d);
  };
  const handlePracticalMethodChange = (val?: string) => {
    const d: DecisionProposal = {
      ...decision,
      practicalMethod: val && val.length > 0 ? val : "",
    };
    handleUpdate(d);
  };
  const handleVerbalMethodCheckedChange = (val: boolean) => {
    const d: DecisionProposal = {
      ...decision,
      verbalMethod: val ? "" : undefined,
    };
    handleUpdate(d);
  };
  const handleVerbalMethodChange = (val?: string) => {
    const d: DecisionProposal = {
      ...decision,
      verbalMethod: val && val.length > 0 ? val : "",
    };
    handleUpdate(d);
  };
  const handleHelpRequiredCheckedChange = (val: boolean) => {
    const d: DecisionProposal = {
      ...decision,
      helpRequired: val ? "" : undefined,
    };
    handleUpdate(d);
  };
  const handleHelpRequiredChange = (val?: string) => {
    const d: DecisionProposal = {
      ...decision,
      helpRequired: val && val.length > 0 ? val : "",
    };
    handleUpdate(d);
  };
  const handleChildEstimateChange = (val?: string) => {
    const d: DecisionProposal = {
      ...decision,
      childEstimate: val && val.length > 0 ? val : undefined,
    };
    handleUpdate(d);
  };
  const handleAppropriateHelpFormatChange = (val?: string) => {
    const d: DecisionProposal = {
      ...decision,
      appropriateHelpFormat: val && val.length > 0 ? val : undefined,
    };
    handleUpdate(d);
  };
  const handleDifficultyTypeChange = (val?: number[]) => {
    const d: DecisionProposal = {
      ...decision,
      difficultyTypes: val && val.length > 0 ? val : undefined,
    };
    handleUpdate(d);
  };
  const handleOtherChange = (val?: string) => {
    const d: DecisionProposal = {
      ...decision,
      other: val && val.length > 0 ? val : undefined,
    };
    handleUpdate(d);
  };
  const handleClassificationChange = (val?: string) => {
    const d: DecisionProposal = {
      ...decision,
      classification: val && val.length > 0 ? val : undefined,
    };
    handleUpdate(d);
  };
  const handleAssociationNumberChange = (val?: string) => {
    const d: DecisionProposal = {
      ...decision,
      associationNumber: val && val.length > 0 ? val : undefined,
    };
    handleUpdate(d);
  };
  const handleDocumentDownload = async (
    doc: Attachment,
    type: DocumentType
  ) => {
    if (doc && doc.id) {
      await getRequestDocument(doc.id, item.id, type);
    }
  };
  const handleUpdate = (item: DecisionProposal) => {
    setDecision(item);

    dispatch(setIsEdited());
  };

  const handleCommitteeChange = (documentCommittees: DocumentCommittee[]) => {
    const d: DecisionProposal = {
      ...decision,
      committeesForDocument: documentCommittees,
    };

    handleUpdate(d);
  };

  return (
    <ContentArea>
      <ToolbarTop
        showAdd={"hide"}
        showBack={"show"}
        showEdit={canEdit() ? "show" : "hide"}
        pageTitle={t("decisionProposal.decisionProposalPageTitle")}
        onClickEdit={handleEditingClick}
        errors={errors}
      />
      <RequestHeader request={item} />
      <FormCard>
        <FormCard cardTitle={t("decisionProposal.dates")}>
          <Row>
            <Col md={4}>
              <DatePicker
                label={t("decisionProposal.determinationStartDate")}
                name={"decisionProposal.determinationStartDate"}
                onChange={(e) =>
                  handleDeterminationStartDateChanged(new Date(e.target.value))
                }
                value={decision.determinationStartDate}
                minYear={new Date().getFullYear()}
                plaintext={!isEditing}
                errorText={
                  errors?.find((e) =>
                    e.propertyName?.startsWith("DeterminationStartDate")
                  )?.message
                }
              />
            </Col>
          </Row>
          <Row>
            <Col md={4}>
              <DatePicker
                label={t("decisionProposal.determinationEndDate")}
                name={"decisionProposal.determinationEndDate"}
                onChange={(e) =>
                  handleDeterminationEndDateChanged(new Date(e.target.value))
                }
                value={decision.determinationEndDate}
                minYear={new Date().getFullYear()}
                plaintext={!isEditing}
                errorText={
                  errors?.find((e) =>
                    e.propertyName?.startsWith("DeterminationEndDate")
                  )?.message
                }
              />
            </Col>
          </Row>
          <Row>
            <Col md={4}>
              <DatePicker
                label={t("decisionProposal.sessionDate")}
                name={"decisionProposal.sessionDate"}
                onChange={(e) =>
                  handleSessionDateChanged(new Date(e.target.value))
                }
                value={decision.sessionDate}
                minYear={new Date().getFullYear()}
                plaintext={!isEditing}
                errorText={
                  errors?.find((e) => e.propertyName?.startsWith("SessionDate"))
                    ?.message
                }
              />
            </Col>
          </Row>
        </FormCard>
        <FormCard cardTitle={t("decisionProposal.requestOptions")}>
          <Row>
            <Col md={12}>
              <Checkbox
                label={t("decisionProposal.isEarlyEnrollmentApproved")}
                name={nameof<typeof decision>("isEarlyEnrollmentApproved")}
                checked={decision.isEarlyEnrollmentApproved}
                onChange={(e) =>
                  handleIsEarlyEnrollmentApprovedChange(e.target.checked)
                }
                plaintext={!isEditing}
                disabled={!(item.type === RequestType.EarlyEnrollment)}
                errorText={
                  errors?.find((e) =>
                    e.propertyName?.startsWith("IsEarlyEnrollmentApproved")
                  )?.message
                }
              />
            </Col>
          </Row>
          <Row>
            <Col md={12}>
              <Checkbox
                label={t("decisionProposal.isEarlyEnrollmentRejected")}
                name={nameof<typeof decision>("isEarlyEnrollmentRejected")}
                checked={decision.isEarlyEnrollmentRejected}
                onChange={(e) =>
                  handleIsEarlyEnrollmentRejectedChange(e.target.checked)
                }
                plaintext={!isEditing}
                disabled={!(item.type === RequestType.EarlyEnrollment)}
                errorText={
                  errors?.find((e) =>
                    e.propertyName?.startsWith("IsEarlyEnrollmentRejected")
                  )?.message
                }
              />
            </Col>
          </Row>
          <Row>
            <Col md={12}>
              <Checkbox
                label={t("decisionProposal.isEnrollmentPostponement")}
                name={nameof<typeof decision>("isEnrollmentPostponement")}
                checked={decision.isEnrollmentPostponement}
                onChange={(e) =>
                  handleIsEnrollmentPostponementChange(e.target.checked)
                }
                plaintext={!isEditing}
                disabled={
                  !(
                    item.type === RequestType.RegularEnrollment ||
                    item.type ===
                      RequestType.RegularEnrollmentForChildrenWithLearningDifficulties
                  ) ||
                  decision.isAppropriateProgram ||
                  decision.isPreparatoryCourse
                }
                errorText={
                  errors?.find((e) =>
                    e.propertyName?.startsWith("IsEnrollmentPostponement")
                  )?.message
                }
              />
            </Col>
          </Row>
          <Row>
            <Col md={12}>
              <Checkbox
                label={t("decisionProposal.isAppropriateProgram")}
                name={nameof<typeof decision>("isAppropriateProgram")}
                checked={decision.isAppropriateProgram}
                onChange={(e) =>
                  handleIsAppropriateProgramChange(e.target.checked)
                }
                plaintext={!isEditing}
                disabled={
                  !(
                    item.type === RequestType.RegularEnrollment ||
                    item.type ===
                      RequestType.RegularEnrollmentForChildrenWithLearningDifficulties
                  ) || decision.isEnrollmentPostponement
                }
                errorText={
                  errors?.find((e) =>
                    e.propertyName?.startsWith("IsAppropriateProgram")
                  )?.message
                }
              />
            </Col>
          </Row>
          <Row>
            <Col md={12}>
              <Checkbox
                label={t("decisionProposal.isPreparatoryCourse")}
                name={nameof<typeof decision>("isPreparatoryCourse")}
                checked={decision.isPreparatoryCourse}
                onChange={(e) =>
                  handleIsPreparatoryCourseChange(e.target.checked)
                }
                plaintext={!isEditing}
                disabled={
                  !(
                    item.type === RequestType.RegularEnrollment ||
                    item.type ===
                      RequestType.RegularEnrollmentForChildrenWithLearningDifficulties ||
                    item.type === RequestType.EarlyEnrollment
                  ) || decision.isEnrollmentPostponement
                }
                errorText={
                  errors?.find((e) =>
                    e.propertyName?.startsWith("IsPreparatoryCourse")
                  )?.message
                }
              />
            </Col>
          </Row>
        </FormCard>
        {decision?.committeesForDocument &&
          initialDocumentCommittees &&
          !!initialDocumentCommittees.length && (
            <CommissionSelection
              committees={decision.committeesForDocument}
              onCommitteeChange={handleCommitteeChange}
              initialDocumentCommittees={initialDocumentCommittees}
              isEditing={isEditing}
            />
          )}
        <FormCard cardTitle={t("decisionProposal.methods")}>
          <Row>
            <Col md={3}>
              <Checkbox
                label={t("decisionProposal.practicalMethod")}
                checked={decision.practicalMethod != undefined}
                onChange={(e) =>
                  handlePracticalMethodCheckedChange(e.target.checked)
                }
                plaintext={!isEditing}
              />
            </Col>
            <Col md={9}>
              <TextArea
                ariaLabel={t("decisionProposal.practicalMethod")}
                name={nameof<typeof decision>("practicalMethod")}
                value={decision.practicalMethod}
                onChange={(e) => handlePracticalMethodChange(e.target.value)}
                maxLength={1500}
                readonly={!isEditing}
                disabled={isEditing && decision.practicalMethod == undefined}
                errorText={
                  errors?.find((e) =>
                    e.propertyName?.startsWith("PracticalMethod")
                  )?.message
                }
                rows={5}
              />
            </Col>
          </Row>
          <Row>
            <Col md={3}>
              <Checkbox
                label={t("decisionProposal.verbalMethod")}
                checked={decision.verbalMethod != undefined}
                onChange={(e) =>
                  handleVerbalMethodCheckedChange(e.target.checked)
                }
                plaintext={!isEditing}
              />
            </Col>
            <Col md={9}>
              <TextArea
                ariaLabel={t("decisionProposal.verbalMethod")}
                name={nameof<typeof decision>("verbalMethod")}
                value={decision.verbalMethod}
                onChange={(e) => handleVerbalMethodChange(e.target.value)}
                maxLength={1500}
                readonly={!isEditing}
                disabled={isEditing && decision.verbalMethod == undefined}
                errorText={
                  errors?.find((e) =>
                    e.propertyName?.startsWith("VerbalMethod")
                  )?.message
                }
                rows={5}
              />
            </Col>
          </Row>
          <Row>
            <Col md={3}>
              <Checkbox
                label={t("decisionProposal.helpRequired")}
                checked={decision.helpRequired != undefined}
                onChange={(e) =>
                  handleHelpRequiredCheckedChange(e.target.checked)
                }
                plaintext={!isEditing}
              />
            </Col>
            <Col md={9}>
              <TextArea
                ariaLabel={t("decisionProposal.helpRequired")}
                name={nameof<typeof decision>("helpRequired")}
                value={decision.helpRequired}
                onChange={(e) => handleHelpRequiredChange(e.target.value)}
                maxLength={1500}
                readonly={!isEditing}
                disabled={isEditing && decision.helpRequired == undefined}
                errorText={
                  errors?.find((e) =>
                    e.propertyName?.startsWith("HelpRequired")
                  )?.message
                }
                rows={5}
              />
            </Col>
          </Row>
        </FormCard>
        <FormCard cardTitle={t("decisionProposal.notes")}>
          <Row>
            <Col md={12}>
              <TextArea
                ariaLabel={t("decisionProposal.childEstimate")}
                label={t("decisionProposal.childEstimate")}
                value={decision.childEstimate}
                onChange={(e) => handleChildEstimateChange(e.target.value)}
                maxLength={1000}
                readonly={!isEditing}
                errorText={
                  errors?.find((e) => e.propertyName === "ChildEstimate")
                    ?.message
                }
                rows={5}
              />
            </Col>
          </Row>
          <Row>
            <Col md={12}>
              <TextField
                label={t("decisionProposal.appropriateHelpFormat")}
                name={nameof<typeof decision>("appropriateHelpFormat")}
                value={decision.appropriateHelpFormat}
                onChange={(e) =>
                  handleAppropriateHelpFormatChange(e.target.value)
                }
                maxLength={255}
                plaintext={!isEditing}
                errorText={
                  errors?.find((e) =>
                    e.propertyName?.startsWith("AppropriateHelpFormat")
                  )?.message
                }
              />
            </Col>
          </Row>
          <Row>
            <Col md={12}>
              <Select
                label={t("decisionProposal.difficultyTypes")}
                name={nameof<typeof decision>("difficultyTypes")}
                value={decision.difficultyTypes}
                onChange={(e) => handleDifficultyTypeChange(e.target.value)}
                selectData={codeBookToSelectData(difficultyTypes)}
                errorText={
                  errors?.find((e) => e.propertyName === "DifficultyTypes")
                    ?.message
                }
                disabled={!isEditing}
                multiple
              />
            </Col>
          </Row>
          <Row>
            <Col md={12}>
              <TextField
                label={t("decisionProposal.other")}
                name={nameof<typeof decision>("other")}
                value={decision.other}
                onChange={(e) => handleOtherChange(e.target.value)}
                maxLength={255}
                plaintext={!isEditing}
                errorText={
                  errors?.find((e) => e.propertyName?.startsWith("Other"))
                    ?.message
                }
              />
            </Col>
          </Row>
        </FormCard>
        <FormCard cardTitle={t("decisionProposal.otherData")}>
          <Row>
            <Col md={6}>
              <TextField
                label={t("decisionProposal.classification")}
                name={nameof<typeof decision>("classification")}
                value={decision.classification}
                onChange={(e) => handleClassificationChange(e.target.value)}
                maxLength={50}
                plaintext={!isEditing}
                errorText={
                  errors?.find((e) =>
                    e.propertyName?.startsWith("Classification")
                  )?.message
                }
              />
            </Col>
            <Col md={6}>
              <TextField
                label={t("decisionProposal.associationNumber")}
                name={nameof<typeof decision>("associationNumber")}
                value={decision.associationNumber}
                onChange={(e) => handleAssociationNumberChange(e.target.value)}
                maxLength={50}
                plaintext={!isEditing}
                errorText={
                  errors?.find((e) =>
                    e.propertyName?.startsWith("AssociationNumber")
                  )?.message
                }
              />
            </Col>
          </Row>
        </FormCard>
        {!isEditing && decision.attachment && (
          <DocumentList
            documents={decision.attachment ? [decision.attachment] : []}
            onDocumentClick={(doc) =>
              handleDocumentDownload(doc, DocumentType.DecisionProposal)
            }
            removable={false}
          />
        )}
      </FormCard>
      <ToolbarEdit
        showCancel={"show"}
        showSave={isEditing ? "show" : "hide"}
        redirectUrlSaveOK={() => requestAdminDetailsUrl(+id)}
        handleSave={() => saveDecisionProposal({ ...decision, request: item })}
        setSaveErrors={setErrors}
        showCustom={false}
      />
    </ContentArea>
  );
}
